import { AuctionIcon, NotificationsIcon, CloseIcon } from '@f8n/icons';
import { styled } from '@f8n-frontend/stitches';
import { zodResolver } from '@hookform/resolvers/zod';
import { fromUnixTime, getUnixTime, secondsInDay } from 'date-fns';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { match } from 'ts-pattern';
import { Address } from 'viem';
import { useWriteContract, useSimulateContract } from 'wagmi';
import { z } from 'zod';

import ExplainUnlisting from 'components/ExplainUnlisting';
import { MomentTag } from 'components/MomentTag';
import NftTag from 'components/NftTag';
import TransactionModal from 'components/TransactionModal';
import Box from 'components/base/Box';
import Button from 'components/base/Button';
import Divider from 'components/base/Divider';
import Field from 'components/base/Field';
import InputEth from 'components/base/InputEth';
import Modal from 'components/base/Modal';
import Text from 'components/base/Text';
import { DatePicker } from 'components/forms/DatePicker';
import { DurationFields } from 'components/forms/DurationFields';
import AmountBreakDown from 'components/transactions/generic/AmountBreakdown';
import CuratedStorePicker from 'components/worlds/CuratedStorePicker';
import WorldTag from 'components/worlds/WorldTag';
import useAnalytics from 'contexts/analytics/useAnalytics';
import { hasPublicKey } from 'contexts/auth/helpers';
import useAuth from 'contexts/auth/useAuth';
import { LIST_CTA_MAP, LIST_HEADING_MAP } from 'copy/sell';
import useTransactionStore from 'state/stores/transactions';
import { SellIntent, getSellConfig } from 'web3/sell';

import { useCuratedStores } from 'gql/api/queries/curated-stores.generated';
import {
  useNftAndWorldOverview,
  ApiNftAndWorldOverview,
} from 'gql/api/queries/nft-and-world-overview.generated';
import { useMomentByNft } from 'hooks/queries/api/use-moment-by-nft';
import { useQueryEffects } from 'hooks/react-query';
import useModalVisibility from 'hooks/use-modal-visibility';
import { useGenerateMomentSignature } from 'hooks/web3/use-generate-moment-signature';
import { UNIX_EPOCH_DATE } from 'lib/constants';
import { logger } from 'lib/logger';
import report from 'lib/report';
import {
  listSchema,
  sellViaAuctionFormSchema,
  sellViaBuyNowFormSchema,
  GENERIC_PRICE_ERROR_PATH,
  unlistSchema,
} from 'schemas/forms/sell';
import { getAddress } from 'utils/address';
import { getChainAnalyticsSummary } from 'utils/analytics';
import {
  getMomentIdFromCuratedStore,
  getTakeRateFromCuratedStore,
  getWorldFromCuratedStore,
  getWorldIdFromCuratedStore,
  hasCuratedStores,
  isMomentCuratedStore,
  isWorldCuratedStore,
} from 'utils/curated-store';
import { dateTimeToUnix, getUnixTxDeadline } from 'utils/dates/dates';
import { formatETHWithSuffix } from 'utils/formatters';
import { isNumberType, noop } from 'utils/helpers';
import { getTokenFilter } from 'utils/inputs';
import { mapApiMediaToPreviewMediaAsset } from 'utils/media-preview';
import { registerField } from 'utils/react-hook-form';
import {
  extractErrorMessage,
  extractPrepareContractWriteRevertReason,
} from 'utils/revert-reasons';
import { logSignatureError } from 'utils/signature';

import { CuratedStores, CuratedStore } from 'types/CuratedStore';
import { WorldOverview } from 'types/World';
import { ModalOptions } from 'types/modal';

// By default, html forms will attempt a GET request on submit. Use this to prevent that.
const PREVENT_BROWSER_DEFAULT_FORM_SUBMISSION = noop;

const INITIAL_AUCTION_DURATION = secondsInDay;

const MODAL_KEY = 'SELL';

type SellModalOptions = ModalOptions<typeof MODAL_KEY>;

const STEPS = [
  'OVERVIEW',
  'ADD_RESERVE',
  'ADD_BUY_NOW',
  'CONFIRM_UNLIST',
] as const;
type SellModalSteps = (typeof STEPS)[number];

export default function SellModal() {
  const modal = useModalVisibility(MODAL_KEY);

  return (
    <Modal.Root open={modal.open} onOpenChange={modal.onOpenChange}>
      <Modal.Portal>
        <Modal.BlurOverlay />
        <Modal.PositionOverlay>
          <Modal.UnmountListener onUnmount={modal.onUnmount} />
          {modal.config && <SellModalWindowConnected {...modal.config} />}
        </Modal.PositionOverlay>
      </Modal.Portal>
    </Modal.Root>
  );
}

const overviewFormResolver = zodResolver(listSchema);
const buyNowFormResolver = zodResolver(sellViaBuyNowFormSchema);
const reserveFormResolver = zodResolver(sellViaAuctionFormSchema);

type OverviewFormValues = z.input<typeof listSchema>;
type BuyNowFormValues = z.input<typeof sellViaBuyNowFormSchema>;
type AuctionFormValues = z.input<typeof sellViaAuctionFormSchema>;

type SellFormValues = OverviewFormValues & {
  world: WorldOverview | null;
  // This field isn't used directly in the form, but we use it to report an error when neither price is set
  [GENERIC_PRICE_ERROR_PATH]: unknown;
};

function SellModalWindowConnected(props: SellModalOptions) {
  const { nft } = props;

  const chainId = nft.chainId;

  const auth = useAuth();

  const nftFilter = getTokenFilter(nft);
  const nftQuery = useNftAndWorldOverview(nftFilter);
  const momentByNftQuery = useMomentByNft(nftFilter);

  const curatedStoresQuery = useCuratedStores({
    creatorPublicKey: hasPublicKey(auth) ? auth.publicKey : '',
    chainId,
  });

  if (nftQuery.isSuccess && nftQuery.data.nft && momentByNftQuery.isSuccess) {
    const getCuratedStore = (): CuratedStore | null => {
      if (momentByNftQuery.data) {
        return { type: 'MOMENT', moment: momentByNftQuery.data };
      }

      if (nftQuery.data.world) {
        return { type: 'WORLD', world: nftQuery.data.world };
      }
      return null;
    };

    return (
      <SellModalWindow
        contractAddress={nft.contractAddress}
        nft={nftQuery.data.nft}
        tokenId={nft.tokenId}
        curatedStore={getCuratedStore()}
        curatedStores={
          curatedStoresQuery.isSuccess
            ? curatedStoresQuery.data.curatedStores
            : { worlds: [], moments: [] }
        }
        publicKey={hasPublicKey(auth) ? auth.publicKey : ('' as Address)}
      />
    );
  }

  return null;
}

export type SellModalNft = NonNullable<ApiNftAndWorldOverview['nft']>;

type SellModalWindowProps = {
  contractAddress: Address;
  tokenId: number;
  nft: SellModalNft;
  curatedStore: CuratedStore | null;
  curatedStores: CuratedStores;
  publicKey: Address;
};

const PRICE_BUTTON_IDS = {
  buyNow: 'add-buy-now',
  reserve: 'add-reserve',
} as const;

export const SELL_MODAL_TEST_IDS = {
  addBuyPrice: 'add-buy-price',
  addReservePrice: 'add-reserve-price',
  configuredBuyPrice: 'configured-buy-price',
  configuredReservePrice: 'configured-reserve-price',
  datePicker: 'date-picker',
  datePickerTrigger: 'date-picker-input',
  datePickerNextMonth: 'date-picker-next-month',
};

export const SELL_MODAL_HEADING_MAP = {
  LIST: LIST_HEADING_MAP.LIST,
  EDIT: LIST_HEADING_MAP.EDIT,
  UNLIST: LIST_HEADING_MAP.UNLIST,
  AUCTION: 'Set a Reserve price',
  BUY: 'Set a Buy Now price',
};

export const SELL_MODAL_CTA_MAP = {
  DONE: 'Done',
  LIST: LIST_CTA_MAP.LIST,
  EDIT: 'Update listing',
  REVIEW_BEFORE_UNLISTING: 'Review',
  UNLIST: 'Remove listing',
} as const;

/**
 * Note: Exported primarily for testing purposes
 */
export function SellModalWindow(props: SellModalWindowProps) {
  const {
    contractAddress,
    tokenId,
    nft,
    curatedStore: curatedStoreNftIsCurrentlyIn,
    curatedStores,
    publicKey,
  } = props;
  const [step, setStep] = useState<SellModalSteps>('OVERVIEW');
  const analytics = useAnalytics();

  const auth = useAuth();

  const txStore = useTransactionStore();

  const [selectedCuratedStore, setSelectedCuratedStore] =
    useState<CuratedStore | null>(curatedStoreNftIsCurrentlyIn);

  const sellForm = useForm<SellFormValues>({
    resolver: overviewFormResolver,
    defaultValues: {
      auction: nft.activeAuction
        ? {
            duration: nft.activeAuction.duration,
            reservePrice: nft.activeAuction.reservePrice,
          }
        : null,
      buyNow: nft.activeBuyNow ? { price: nft.activeBuyNow.amount } : null,
      saleStartsAt: nft.saleStartsAt ? dateTimeToUnix(nft.saleStartsAt) : null,
      world: curatedStoreNftIsCurrentlyIn
        ? getWorldFromCuratedStore(curatedStoreNftIsCurrentlyIn)
        : null,
    },
  });

  const chainId = nft.chainId;

  const buyNowForm = useForm<BuyNowFormValues>({
    resolver: buyNowFormResolver,
  });

  const auctionForm = useForm<AuctionFormValues>({
    resolver: reserveFormResolver,
    defaultValues: {
      duration: nft.activeAuction
        ? nft.activeAuction.duration
        : INITIAL_AUCTION_DURATION,
    },
  });

  const auction = sellForm.watch('auction');
  const buyNow = sellForm.watch('buyNow');
  const saleStartsAt = sellForm.watch('saleStartsAt');
  const world = sellForm.watch('world');

  const sellFormValues = sellForm.watch();

  const intent = detectUserIntent({ nft, formValues: sellFormValues, step });

  const [txDeadlineUnix] = useState(() => getUnixTxDeadline());

  const generateSignatureQuery = useGenerateMomentSignature(
    {
      selectedCuratedStore,
      publicKey,
      contractAddress,
      tokenId,
      txDeadlineUnix,
      listingType: 'NFT',
    },
    { refetchOnWindowFocus: false }
  );

  const getSellValues = () => {
    if (intent === 'UNLIST') {
      return {
        auctionDuration: null,
        buyPrice: null,
        contractAddress,
        reservePrice: null,
        saleStartsAt: null,
        tokenId: props.tokenId,
        worldId: null,
        selectedCuratedStore: null,
        signatureQuery: generateSignatureQuery,
        chainId,
      };
    }

    const parsedListSchema = listSchema.safeParse(sellFormValues);
    if (parsedListSchema.success) {
      const parsedData = parsedListSchema.data;

      return {
        auctionDuration: parsedData.auction
          ? parsedData.auction.duration
          : null,
        buyPrice: parsedData.buyNow ? parsedData.buyNow.price : null,
        contractAddress,
        reservePrice: parsedData.auction
          ? parsedData.auction.reservePrice
          : null,
        saleStartsAt: parsedData.saleStartsAt,
        tokenId: props.tokenId,
        worldId: parsedData.world ? parsedData.world.id : null,
        selectedCuratedStore,
        signatureQuery: generateSignatureQuery,
        chainId,
      };
    }

    return null;
  };

  const values = getSellValues();

  const config = getSellConfig({
    intent,
    values,
    configOptions: {
      enabled: Boolean(values),
    },
  });

  const simulateSell = useSimulateContract({
    ...config.prepareContractWriteConfig,
  });

  useQueryEffects(simulateSell, {
    onError: (error) => {
      logger.error(
        `sell-modal simulation error ${contractAddress} ${tokenId}`,
        { ...error }
      );
      report(extractPrepareContractWriteRevertReason({ error }));
      if (generateSignatureQuery.isSuccess) {
        logSignatureError({
          error,
          context: {
            contractAddress,
            tokenId,
            txDeadlineUnix,
            selectedCuratedStore,
            publicKey,
            listingType: 'NFT',
          },
        });
      }
    },
  });

  const contractWrite = useWriteContract({
    mutation: {
      onError: (error) => {
        report(error);
      },
      onMutate: (variables) => {
        logger.info('sell-modal onMutate', {
          functionName: variables?.functionName,
          intent,
        });

        if (config.actionName) {
          analytics.track({
            action: config.actionName,
            chain: getChainAnalyticsSummary(chainId),
            name: 'web3_action_prompted',
          });
        } else {
          report(new Error('Failed to initiate sell modal onMutate'));
        }
      },
      onSuccess: (txHash, variables) => {
        logger.info('sell-modal onSuccess', {
          functionName: variables?.functionName,
          intent,
        });

        if (config.trackingCopy && config.actionName) {
          const selectedCuratedStore = values
            ? values.selectedCuratedStore
            : null;

          txStore.startTracking({
            ...config.trackingCopy,
            chainId,
            action: {
              name: config.actionName,
              worldId: getWorldIdFromCuratedStore(selectedCuratedStore),
              momentId: getMomentIdFromCuratedStore(selectedCuratedStore),
            },
            ui: 'toast',
            txHash,
          });
        } else {
          report(new Error('Failed to initiate sell modal onSuccess'));
        }
      },
    },
  });

  useEffect(() => {
    match(step)
      .with('OVERVIEW', () => {
        analytics.track({
          name: 'sell_modal_viewed_overview',
          intent,
        });
      })
      .with('ADD_BUY_NOW', () => {
        analytics.track({
          name: 'sell_modal_viewed_buy_now_form',
          intent,
        });
      })
      .with('ADD_RESERVE', () => {
        analytics.track({
          name: 'sell_modal_viewed_auction_form',
          intent,
        });
      })
      .with('CONFIRM_UNLIST', () => {
        analytics.track({
          name: 'sell_modal_viewed_unlist_confirmation',
        });
      })
      .exhaustive();
  }, [step, intent]);

  const goToOverview = () => setStep('OVERVIEW');

  const media = nft.media ? mapApiMediaToPreviewMediaAsset(nft.media) : null;

  const getPriceButtonId = () => {
    if (!auction) {
      return PRICE_BUTTON_IDS.reserve;
    }

    if (!buyNow) {
      return PRICE_BUTTON_IDS.buyNow;
    }
  };

  const getGenericPriceError = () => {
    if (intent === 'UNLIST' && !world) return;
    return sellForm.formState.errors[GENERIC_PRICE_ERROR_PATH]?.message;
  };

  const getBody = () => {
    switch (step) {
      case 'OVERVIEW':
        return (
          <SellModalBody>
            <NftTag
              collection={nft.collection}
              name={nft.name ?? ''}
              media={media}
            />
            {getCuratedStoreField()}
            <Field
              htmlFor={getPriceButtonId()}
              error={getGenericPriceError()}
              label="Set price"
              required
              touched
            >
              {auction ? (
                <Option
                  data-testid={SELL_MODAL_TEST_IDS.configuredReservePrice}
                  isValid
                >
                  <Box>
                    <Text weight="medium" size={1} color="dim">
                      Reserve
                    </Text>
                    <Text weight="semibold" size={3} color="strong">
                      {formatETHWithSuffix(auction.reservePrice)}
                    </Text>
                  </Box>
                  <Button
                    variant="ghost"
                    icon="standalone"
                    aria-label="Remove Reserve price"
                    css={{ color: '$black50' }}
                    onClick={() => {
                      sellForm.setValue('auction', null, { shouldDirty: true });
                      sellForm.trigger();
                      analytics.track({
                        name: 'sell_modal_removed_reserve_price',
                      });
                    }}
                    type="button"
                  >
                    <CloseIcon />
                  </Button>
                </Option>
              ) : (
                <Option data-testid={SELL_MODAL_TEST_IDS.addReservePrice}>
                  <IconWrapper>
                    <AuctionIcon />
                  </IconWrapper>
                  <OptionText>Reserve</OptionText>
                  <OptionButton
                    onClick={() => {
                      setStep('ADD_RESERVE');
                      analytics.track({
                        name: 'sell_modal_clicked_add_reserve_price',
                      });
                    }}
                    id={PRICE_BUTTON_IDS.reserve}
                  >
                    Add
                  </OptionButton>
                </Option>
              )}
              {buyNow ? (
                <Option
                  data-testid={SELL_MODAL_TEST_IDS.configuredBuyPrice}
                  isValid
                >
                  <Box>
                    <Text weight="medium" size={1} color="dim">
                      Buy Now
                    </Text>
                    <Text weight="semibold" size={3} color="strong">
                      {formatETHWithSuffix(buyNow.price)}
                    </Text>
                  </Box>
                  <Button
                    variant="ghost"
                    icon="standalone"
                    aria-label="Remove Buy Now price"
                    css={{ color: '$black50' }}
                    onClick={() => {
                      sellForm.setValue('buyNow', null, { shouldDirty: true });
                      sellForm.trigger();
                      analytics.track({
                        name: 'sell_modal_removed_buy_price',
                      });
                    }}
                    type="button"
                  >
                    <CloseIcon />
                  </Button>
                </Option>
              ) : (
                <Option data-testid={SELL_MODAL_TEST_IDS.addBuyPrice}>
                  <IconWrapper>
                    <NotificationsIcon />
                  </IconWrapper>
                  <OptionText>Buy Now</OptionText>
                  <OptionButton
                    onClick={() => {
                      setStep('ADD_BUY_NOW');
                      analytics.track({
                        name: 'sell_modal_clicked_add_buy_price',
                      });
                    }}
                    id={PRICE_BUTTON_IDS.buyNow}
                  >
                    Add
                  </OptionButton>
                </Option>
              )}
            </Field>
            <DatePicker
              required
              label="Becomes available"
              selected={
                isNumberType(saleStartsAt)
                  ? fromUnixTime(saleStartsAt)
                  : UNIX_EPOCH_DATE // used to force DatePicker to display Now by default
              }
              onSelect={(date) => {
                if (!date) {
                  return sellForm.setValue('saleStartsAt', null, {
                    shouldDirty: true,
                  });
                }

                // Note that when `immediately` is selected, the date is set to the Unix epoch (Jan 1st 1970).
                const unixTime = getUnixTime(date);

                if (unixTime === 0) {
                  return sellForm.setValue('saleStartsAt', null, {
                    shouldDirty: true,
                  });
                } else {
                  sellForm.setValue('saleStartsAt', unixTime, {
                    shouldDirty: true,
                  });
                }
              }}
            />
          </SellModalBody>
        );

      case 'ADD_RESERVE':
        return (
          <SellModalBody>
            <TransactionModal.InputWrapper>
              {/* TODO: use Field props instead of custom Text components for labels */}
              <Text
                as="label"
                htmlFor="reserve-price"
                size={2}
                weight="semibold"
                lineHeight={0}
              >
                Reserve price
              </Text>
              <Text color="dim" weight="regular" size={1}>
                Minimum price for an auction to start.
              </Text>
              <InputEth
                {...registerField(auctionForm, 'reservePrice')}
                required
                id="reserve-price"
                autoFocus
              />

              <AmountBreakDown.FeesConnected
                chainId={chainId}
                currentUserPublicKey={
                  hasPublicKey(auth) ? auth.publicKey : null
                }
                contractAddress={contractAddress}
                tokenId={tokenId}
                price={Number(auctionForm.watch('reservePrice'))}
                worldId={world ? world.id : null}
                takeRateInBasisPoints={
                  selectedCuratedStore
                    ? getTakeRateFromCuratedStore(selectedCuratedStore)
                    : null
                }
              />
              {intent === 'LIST' && (
                <>
                  <Divider />
                  <Box css={{ paddingTop: '$3' }}>
                    <DurationFields
                      activeDurationInSeconds={auctionForm.watch(
                        'duration',
                        INITIAL_AUCTION_DURATION
                      )}
                      onChange={(seconds) =>
                        auctionForm.setValue('duration', seconds)
                      }
                    />
                  </Box>
                </>
              )}
            </TransactionModal.InputWrapper>
          </SellModalBody>
        );

      case 'ADD_BUY_NOW':
        return (
          <SellModalBody>
            <TransactionModal.InputWrapper>
              {/* TODO: use Field props instead of custom Text components for labels */}
              <Text
                size={2}
                weight="semibold"
                lineHeight={0}
                as="label"
                htmlFor="buy-now-price"
              >
                Buy Now price
              </Text>
              <Text color="dim" weight="regular" size={1}>
                Allows collectors to purchase the NFT instantly.
              </Text>
              <InputEth
                {...registerField(buyNowForm, 'price')}
                required
                id="buy-now-price"
                autoFocus
              />
              <AmountBreakDown.FeesConnected
                chainId={chainId}
                currentUserPublicKey={
                  hasPublicKey(auth) ? auth.publicKey : null
                }
                contractAddress={contractAddress}
                tokenId={tokenId}
                price={Number(buyNowForm.watch('price'))}
                worldId={world ? world.id : null}
                takeRateInBasisPoints={
                  selectedCuratedStore
                    ? getTakeRateFromCuratedStore(selectedCuratedStore)
                    : null
                }
              />
            </TransactionModal.InputWrapper>
          </SellModalBody>
        );

      case 'CONFIRM_UNLIST': {
        return (
          <SellModalBody>
            <ExplainUnlisting nft={nft} media={media} />
          </SellModalBody>
        );
      }
    }
  };

  const getCuratedStoreField = () => {
    if (!hasCuratedStores(curatedStores)) {
      return null;
    }

    if (selectedCuratedStore) {
      return (
        <Field label={null}>
          <Option isValid>
            {match(selectedCuratedStore)
              .when(isWorldCuratedStore, ({ world }) => (
                <WorldTag world={world} type="static" showCuratorFee />
              ))
              .when(isMomentCuratedStore, ({ moment }) => (
                <MomentTag moment={moment} world={moment.world} type="static" />
              ))
              .exhaustive()}
            <Button
              variant="ghost"
              icon="standalone"
              aria-label="Remove gallery"
              css={{ color: '$black50' }}
              onClick={() => {
                setSelectedCuratedStore(null);
                sellForm.setValue('world', null, { shouldDirty: true });
                sellForm.trigger();
                analytics.track({
                  name: 'sell_modal_removed_world',
                });
              }}
              type="button"
            >
              <CloseIcon />
            </Button>
          </Option>
        </Field>
      );
    }

    return (
      <Field htmlFor="curated-stores" label={null} size={2}>
        <CuratedStorePicker
          id="curated-stores"
          selectCuratedStore={(curatedStore) => {
            setSelectedCuratedStore(curatedStore);
            sellForm.setValue('world', getWorldFromCuratedStore(curatedStore), {
              shouldDirty: true,
            });
          }}
          selectedCuratedStore={null}
          curatedStores={curatedStores}
        />
      </Field>
    );
  };

  const getHeader = () => {
    switch (step) {
      case 'OVERVIEW': {
        return (
          <Modal.Header
            title={
              intent === 'LIST'
                ? SELL_MODAL_HEADING_MAP.LIST
                : SELL_MODAL_HEADING_MAP.EDIT
            }
            onBackClick={null}
          />
        );
      }

      case 'ADD_RESERVE': {
        return (
          <Modal.Header
            title={SELL_MODAL_HEADING_MAP.AUCTION}
            onBackClick={() => {
              goToOverview();
              auctionForm.reset();
            }}
          />
        );
      }

      case 'ADD_BUY_NOW': {
        return (
          <Modal.Header
            title={SELL_MODAL_HEADING_MAP.BUY}
            onBackClick={() => {
              goToOverview();
              buyNowForm.reset();
            }}
          />
        );
      }

      case 'CONFIRM_UNLIST': {
        return (
          <Modal.Header
            title={SELL_MODAL_HEADING_MAP.UNLIST}
            onBackClick={() => {
              goToOverview();
            }}
          />
        );
      }
    }
  };

  const getSubmitTransactionButton = (options: {
    label: string;
    formSchema: Zod.Schema | null;
  }) => {
    const { formSchema } = options;

    return (
      <TransactionModal.TransactionButtonWithContractApproval
        chainId={chainId}
        onApprovalSuccess={simulateSell.refetch}
        contractAddress={getAddress(contractAddress)}
        isLoading={contractWrite.isPending || Boolean(contractWrite.data)}
        isDisabled={!simulateSell.isSuccess}
        write={() => {
          if (simulateSell.isSuccess) {
            contractWrite.writeContract(simulateSell.data.request);
          }
        }}
        error={
          formSchema
            ? extractErrorMessage({
                simulateError: simulateSell.error,
                parsedZodSchema: formSchema.safeParse(sellFormValues),
              })
            : extractPrepareContractWriteRevertReason(simulateSell)
        }
        label={options.label}
      />
    );
  };

  const getFooter = () => {
    const hasListing = nft.activeAuction || nft.activeBuyNow;

    switch (step) {
      case 'OVERVIEW': {
        if (intent === 'EDIT') {
          if (sellForm.formState.isDirty === false) {
            return (
              <>
                {hasListing && (
                  <Button
                    type="button"
                    variant="outline"
                    css={{ color: '$red4' }}
                    onClick={() => {
                      setStep('CONFIRM_UNLIST');
                    }}
                  >
                    {SELL_MODAL_CTA_MAP.UNLIST}
                  </Button>
                )}
                <Modal.Close asChild>
                  <Button type="button" variant="outline">
                    {SELL_MODAL_CTA_MAP.DONE}
                  </Button>
                </Modal.Close>
              </>
            );
          }

          return getSubmitTransactionButton({
            label: SELL_MODAL_CTA_MAP.EDIT,
            formSchema: listSchema,
          });
        }

        if (intent === 'UNLIST') {
          return (
            <Button variant="primary" type="submit">
              {SELL_MODAL_CTA_MAP.REVIEW_BEFORE_UNLISTING}
            </Button>
          );
        }

        return getSubmitTransactionButton({
          label: SELL_MODAL_CTA_MAP.LIST,
          formSchema: listSchema,
        });
      }

      case 'ADD_RESERVE':
        return (
          <Button variant="primary" type="submit">
            Set price
          </Button>
        );
      case 'ADD_BUY_NOW':
        return (
          <Button variant="primary" type="submit">
            Set price
          </Button>
        );
      case 'CONFIRM_UNLIST': {
        // TODO: allow user to submit even if no price here...
        return getSubmitTransactionButton({
          label: SELL_MODAL_CTA_MAP.UNLIST,
          formSchema: null,
        });
      }
    }
  };

  const getSubmitHandler = () => {
    switch (step) {
      case 'OVERVIEW': {
        const onInvalidSubmitHanlder = () => {
          if (intent !== 'UNLIST') return;
          const parsedUnlistSchema = unlistSchema.safeParse(sellFormValues);

          if (parsedUnlistSchema.success) {
            setStep('CONFIRM_UNLIST');
          }
        };

        return sellForm.handleSubmit(
          PREVENT_BROWSER_DEFAULT_FORM_SUBMISSION,
          onInvalidSubmitHanlder
        );
      }
      case 'ADD_RESERVE':
        return auctionForm.handleSubmit((auctionConfig) => {
          sellForm.setValue('auction', auctionConfig, { shouldDirty: true });
          sellForm.trigger();
          auctionForm.reset();
          analytics.track({
            name: 'sell_modal_configured_auction',
          });
          goToOverview();
        });
      case 'ADD_BUY_NOW':
        return buyNowForm.handleSubmit((buyNowConfig) => {
          sellForm.setValue('buyNow', buyNowConfig, { shouldDirty: true });
          sellForm.trigger();
          buyNowForm.reset();
          analytics.track({
            name: 'sell_modal_configured_buy_now',
          });
          goToOverview();
        });
      case 'CONFIRM_UNLIST': {
        return sellForm.handleSubmit(PREVENT_BROWSER_DEFAULT_FORM_SUBMISSION);
      }
    }
  };

  return (
    <TransactionModal
      chainId={chainId}
      preSignPrompt={{
        type: 'custom',
        copy: {
          description: 'Sign a message to sell this NFT.',
        },
      }}
    >
      <form onSubmit={getSubmitHandler()}>
        <TransactionModal.Content
          action={config.actionName || 'list-nft'}
          chainId={chainId}
          txHash={contractWrite.data || null}
          header={getHeader()}
          footer={<Modal.Footer>{getFooter()}</Modal.Footer>}
        >
          {getBody()}
        </TransactionModal.Content>
      </form>
    </TransactionModal>
  );
}

const detectUserIntent = (options: {
  nft: SellModalNft;
  formValues: SellFormValues;
  step: SellModalSteps;
}): SellIntent => {
  const { nft, formValues, step } = options;

  if (step === 'CONFIRM_UNLIST') {
    return 'UNLIST';
  }

  const nftIsListed = Boolean(nft.activeAuction) || Boolean(nft.activeBuyNow);

  // User is attempting to unlist the NFT
  if (nftIsListed && !formValues.auction && !formValues.buyNow) return 'UNLIST';

  // No current listings, user is attempting to list the NFT for sale
  if (!nft.activeAuction && !nft.activeBuyNow) return 'LIST';

  return 'EDIT';
};

const SellModalBody = styled(TransactionModal.Body, {
  gap: '$6',
  paddingBottom: '$6',
});

const IconWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '$black5',
  width: 38,
  height: 38,
  borderRadius: '50%',
});

const Option = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$3',
  border: '1px solid $black5',
  padding: '$3',
  borderRadius: '$3',

  '& + &': { marginTop: '$2' },

  variants: {
    isValid: {
      true: {
        borderColor: 'transparent',
        background: '$black5',
        justifyContent: 'space-between',
      },
    },
  },
});
const OptionHeading = styled(Text, {
  marginTop: '$4',
});

OptionHeading.defaultProps = {
  color: 'strong',
  size: 1,
  weight: 'semibold',
};
const OptionText = styled(Text, {});

OptionText.defaultProps = {
  color: 'strong',
  size: 2,
  weight: 'semibold',
};

const OptionButton = styled(Button, {
  marginLeft: 'auto',
});

OptionButton.defaultProps = {
  size: 0,
  variant: 'primary',
  type: 'button',
};
